import { useMemo } from 'react';
import { nativeBeaconViews } from '../components/nativeBeaconViews';
import { BeaconTab } from '../types';
import {
  AvailableBeaconView,
  BeaconView,
  BeaconViewKind,
  SaiDashboardType,
  useGetAvailableBeaconViewsQuery,
} from '../../../graphQL';

type UseGetBeaconTabsResult =
  | { status: 'loading' }
  | { status: 'error'; error: string }
  | { status: 'success'; tabs: BeaconTab[] };

/** The Beacon views the frontend shows, in display order. */
const BEACON_VIEW_DISPLAY: { view: BeaconView; displayName: string }[] = [
  { view: BeaconView.Overview, displayName: 'Overview' },
  { view: BeaconView.SaiMonitoring, displayName: 'Monitoring' },
  { view: BeaconView.Students, displayName: 'Students' },
  { view: BeaconView.SurveyResults, displayName: 'Survey Results' },
  { view: BeaconView.PopulationHealth, displayName: 'Population Health' },
  { view: BeaconView.ResourceDemands, displayName: 'Resource Demands' },
];

const SAI_DASHBOARD_TYPES: string[] = Object.values(SaiDashboardType);

const isSaiDashboardType = (view: BeaconView): view is SaiDashboardType =>
  SAI_DASHBOARD_TYPES.includes(view);

/**
 * The tabs to show for the available views, in display order. Each view uses its native component
 * when one is available and registered, otherwise its Hex embed, otherwise no tab.
 */
const toTabs = (availableViews: AvailableBeaconView[]): BeaconTab[] => {
  const isAvailable = (view: BeaconView, kind: BeaconViewKind) =>
    availableViews.some(available => available.view === view && available.kind === kind);

  return BEACON_VIEW_DISPLAY.flatMap(({ view, displayName }): BeaconTab[] => {
    if (isAvailable(view, BeaconViewKind.Native) && nativeBeaconViews[view]) {
      return [{ kind: BeaconViewKind.Native, key: view, displayName }];
    }
    if (isAvailable(view, BeaconViewKind.HexEmbed) && isSaiDashboardType(view)) {
      return [{ kind: BeaconViewKind.HexEmbed, key: view, displayName }];
    }
    return [];
  });
};

/**
 * Returns the Beacon tabs to show, in display order. The API decides which views are available to
 * the provider and how each can be implemented; this decides how they are labeled, ordered, and
 * which implementation to use.
 */
export const useGetBeaconTabs = (): UseGetBeaconTabsResult => {
  const { data, loading, error } = useGetAvailableBeaconViewsQuery();
  const availableViews = data?.getAvailableBeaconViews.views;

  const tabs = useMemo(() => toTabs(availableViews ?? []), [availableViews]);

  if (loading) {
    return { status: 'loading' };
  }

  if (error) {
    return { status: 'error', error: error.message };
  }

  return { status: 'success', tabs };
};
